import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  TrendingUp, Award, AlertCircle, BarChart3,
  Brain, Zap, Target, Star, ChevronRight, RefreshCw,
  ArrowUpRight, ArrowDownRight, Search, Sparkles,
  FileText, MessageSquare, GitBranch, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';

export const AIInsightsTab = ({ employees, reviews }) => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeAnalysis, setEmployeeAnalysis] = useState(null);
  const [reviewDraft, setReviewDraft] = useState(null);
  const [sentimentResult, setSentimentResult] = useState(null);
  const [summaryResult, setSummaryResult] = useState(null);
  const [growthResult, setGrowthResult] = useState(null);
  const [detailDialog, setDetailDialog] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [bulkAnalyzing, setBulkAnalyzing] = useState(false);
  const [externalMetricsDialog, setExternalMetricsDialog] = useState(false);
  const [extMetrics, setExtMetrics] = useState({
    sales_numbers: '', project_delivery_rate: '', client_feedback_score: '',
    task_completion_rate: '', notes: ''
  });

  useEffect(() => { fetchDashboard(); }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await api.get('/performance/ai/dashboard');
      setDashboard(res.data);
    } catch (err) { console.error('Dashboard error:', err); }
    finally { setLoading(false); }
  };

  const analyzeEmployee = async (empId) => {
    setAnalyzing(true); setEmployeeAnalysis(null);
    try {
      const res = await api.post(`/performance/ai/analyze/${empId}`);
      setEmployeeAnalysis(res.data);
      toast.success('AI Analysis completed');
      fetchDashboard();
    } catch (err) { toast.error(err.response?.data?.detail || 'Analysis failed'); }
    finally { setAnalyzing(false); }
  };

  const generateReviewDraft = async (reviewId) => {
    setAnalyzing(true); setReviewDraft(null);
    try {
      const res = await api.post(`/performance/ai/generate-review-draft/${reviewId}`);
      setReviewDraft(res.data); setDetailDialog('review-draft');
      toast.success('Review draft generated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to generate draft'); }
    finally { setAnalyzing(false); }
  };

  const analyzeSentiment = async (reviewId) => {
    setAnalyzing(true); setSentimentResult(null);
    try {
      const res = await api.post(`/performance/ai/sentiment-analysis/${reviewId}`);
      setSentimentResult(res.data); setDetailDialog('sentiment');
      toast.success('Sentiment analysis completed');
    } catch (err) { toast.error(err.response?.data?.detail || 'Sentiment analysis failed'); }
    finally { setAnalyzing(false); }
  };

  const generate360Summary = async (empId) => {
    setAnalyzing(true); setSummaryResult(null);
    try {
      const res = await api.post(`/performance/ai/360-summary/${empId}`);
      setSummaryResult(res.data); setDetailDialog('360-summary');
      toast.success('360 summary generated');
    } catch (err) { toast.error(err.response?.data?.detail || '360 summary failed'); }
    finally { setAnalyzing(false); }
  };

  const predictGrowth = async (empId) => {
    setAnalyzing(true); setGrowthResult(null);
    try {
      const res = await api.post(`/performance/ai/growth-prediction/${empId}`);
      setGrowthResult(res.data); setDetailDialog('growth');
      toast.success('Growth prediction generated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Prediction failed'); }
    finally { setAnalyzing(false); }
  };

  const bulkAnalyze = async () => {
    setBulkAnalyzing(true);
    try {
      const res = await api.post('/performance/ai/bulk-analyze?limit=10');
      toast.success(`Analyzed ${res.data.analyzed} employees, ${res.data.failed} failed`);
      fetchDashboard();
    } catch (err) { toast.error('Bulk analysis failed'); }
    finally { setBulkAnalyzing(false); }
  };

  const saveExternalMetrics = async () => {
    if (!selectedEmployee) return;
    try {
      const payload = {};
      if (extMetrics.sales_numbers) payload.sales_numbers = parseFloat(extMetrics.sales_numbers);
      if (extMetrics.project_delivery_rate) payload.project_delivery_rate = parseFloat(extMetrics.project_delivery_rate);
      if (extMetrics.client_feedback_score) payload.client_feedback_score = parseFloat(extMetrics.client_feedback_score);
      if (extMetrics.task_completion_rate) payload.task_completion_rate = parseFloat(extMetrics.task_completion_rate);
      if (extMetrics.notes) payload.notes = extMetrics.notes;
      await api.post(`/performance/ai/external-metrics/${selectedEmployee.id}`, payload);
      toast.success('External metrics saved');
      setExternalMetricsDialog(false);
      setExtMetrics({ sales_numbers: '', project_delivery_rate: '', client_feedback_score: '', task_completion_rate: '', notes: '' });
    } catch (err) { toast.error('Failed to save metrics'); }
  };

  const getTierColor = (tier) => ({
    'Exceptional': 'bg-emerald-100 text-emerald-700', 'High Performer': 'bg-blue-100 text-blue-700',
    'Meets Expectations': 'bg-amber-100 text-amber-700', 'Needs Improvement': 'bg-orange-100 text-orange-700',
    'Underperforming': 'bg-red-100 text-red-700'
  }[tier] || 'bg-slate-100 text-slate-700');

  const getScoreColor = (score) => score >= 80 ? 'text-emerald-600' : score >= 60 ? 'text-blue-600' : score >= 40 ? 'text-amber-600' : 'text-red-600';

  const filteredEmployees = employees.filter(emp =>
    emp.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    emp.employee_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    emp.department?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-500 rounded-lg"><Brain className="h-5 w-5 text-white" /></div>
              <div><p className="text-sm text-indigo-600">Analyzed</p><p className="text-2xl font-bold text-indigo-900" data-testid="ai-total-analyzed">{dashboard?.total_analyzed || 0}</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500 rounded-lg"><TrendingUp className="h-5 w-5 text-white" /></div>
              <div><p className="text-sm text-emerald-600">Avg Score</p><p className="text-2xl font-bold text-emerald-900">{dashboard?.average_score || 0}</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500 rounded-lg"><Award className="h-5 w-5 text-white" /></div>
              <div><p className="text-sm text-amber-600">Promotion Ready</p><p className="text-2xl font-bold text-amber-900">{dashboard?.promotion_candidates?.length || 0}</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500 rounded-lg"><AlertCircle className="h-5 w-5 text-white" /></div>
              <div><p className="text-sm text-red-600">Retention Risk</p><p className="text-2xl font-bold text-red-900">{dashboard?.retention_risks?.length || 0}</p></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tier Distribution */}
      {dashboard?.tier_distribution && (
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-lg flex items-center gap-2"><BarChart3 className="h-5 w-5 text-indigo-600" /> Performance Tier Distribution</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-5 gap-3">
              {Object.entries(dashboard.tier_distribution).map(([tier, count]) => (
                <div key={tier} className="text-center">
                  <div className={`text-3xl font-bold ${getScoreColor(tier === 'Exceptional' ? 90 : tier === 'High Performer' ? 75 : tier === 'Meets Expectations' ? 55 : 35)}`}>{count}</div>
                  <p className="text-xs text-slate-500 mt-1">{tier}</p>
                  <Progress value={dashboard.total_analyzed > 0 ? (count / dashboard.total_analyzed) * 100 : 0} className="h-2 mt-2" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top/Low/Promotion */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-emerald-700"><ArrowUpRight className="h-4 w-4" /> Top Performers</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {dashboard?.top_performers?.slice(0, 5).map((emp, idx) => (
              <div key={idx} className="flex items-center justify-between p-2 bg-emerald-50 rounded">
                <div><p className="text-sm font-medium">{emp.employee_name}</p><p className="text-xs text-slate-500">{emp.department}</p></div>
                <Badge className="bg-emerald-100 text-emerald-700">{emp.score}</Badge>
              </div>
            )) || <p className="text-sm text-slate-400 text-center py-4">Run analysis first</p>}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-red-700"><ArrowDownRight className="h-4 w-4" /> Needs Attention</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {dashboard?.low_performers?.slice(0, 5).map((emp, idx) => (
              <div key={idx} className="flex items-center justify-between p-2 bg-red-50 rounded">
                <div><p className="text-sm font-medium">{emp.employee_name}</p><p className="text-xs text-slate-500">{emp.department}</p></div>
                <Badge className="bg-red-100 text-red-700">{emp.score}</Badge>
              </div>
            )) || <p className="text-sm text-slate-400 text-center py-4">Run analysis first</p>}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-amber-700"><Award className="h-4 w-4" /> Promotion Candidates</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {dashboard?.promotion_candidates?.slice(0, 5).map((emp, idx) => (
              <div key={idx} className="flex items-center justify-between p-2 bg-amber-50 rounded">
                <div><p className="text-sm font-medium">{emp.employee_name}</p><p className="text-xs text-slate-500">{emp.designation}</p></div>
                <Badge className="bg-amber-100 text-amber-700">Ready</Badge>
              </div>
            )) || <p className="text-sm text-slate-400 text-center py-4">Run analysis first</p>}
          </CardContent>
        </Card>
      </div>

      {/* Employee AI Actions */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2"><Sparkles className="h-5 w-5 text-indigo-600" /> AI Employee Analysis</CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={bulkAnalyze} disabled={bulkAnalyzing} data-testid="bulk-analyze-btn">
                {bulkAnalyzing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Zap className="h-4 w-4 mr-1" />} Bulk Analyze (10)
              </Button>
              <Button variant="outline" size="sm" onClick={fetchDashboard}><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Search employees..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" data-testid="ai-employee-search" autoComplete="off" />
          </div>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {filteredEmployees.slice(0, 20).map((emp) => {
              const empReview = reviews.find(r => r.employee_id === emp.id);
              return (
                <div key={emp.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                      {emp.name?.substring(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{emp.name}</p>
                      <p className="text-xs text-slate-500">{emp.employee_code} | {emp.department} | {emp.designation}</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => { setSelectedEmployee(emp); analyzeEmployee(emp.id); }} disabled={analyzing} title="AI Performance Analysis" data-testid={`analyze-btn-${emp.id}`}>
                      <Brain className="h-4 w-4 text-indigo-600" />
                    </Button>
                    {empReview && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => { setSelectedEmployee(emp); generateReviewDraft(empReview.id); }} disabled={analyzing} title="Generate Review Draft">
                          <FileText className="h-4 w-4 text-blue-600" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => { setSelectedEmployee(emp); analyzeSentiment(empReview.id); }} disabled={analyzing} title="Sentiment Analysis">
                          <MessageSquare className="h-4 w-4 text-amber-600" />
                        </Button>
                      </>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => { setSelectedEmployee(emp); generate360Summary(emp.id); }} disabled={analyzing} title="360 Summary">
                      <GitBranch className="h-4 w-4 text-purple-600" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => { setSelectedEmployee(emp); predictGrowth(emp.id); }} disabled={analyzing} title="Growth Prediction">
                      <TrendingUp className="h-4 w-4 text-emerald-600" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => { setSelectedEmployee(emp); setExternalMetricsDialog(true); }} title="Add External Metrics">
                      <Target className="h-4 w-4 text-slate-500" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Analysis Result */}
      {employeeAnalysis && (
        <Card className="border-indigo-200 bg-gradient-to-br from-white to-indigo-50" data-testid="ai-analysis-result">
          <CardHeader className="pb-3"><CardTitle className="text-lg flex items-center gap-2"><Brain className="h-5 w-5 text-indigo-600" /> AI Analysis: {employeeAnalysis.employee_name}</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                <p className={`text-4xl font-bold ${getScoreColor(employeeAnalysis.analysis.overall_score)}`}>{employeeAnalysis.analysis.overall_score}</p>
                <p className="text-sm text-slate-500 mt-1">Overall Score</p>
                <Badge className={`mt-2 ${getTierColor(employeeAnalysis.analysis.performance_tier)}`}>{employeeAnalysis.analysis.performance_tier}</Badge>
              </div>
              <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                <p className={`text-4xl font-bold ${getScoreColor(employeeAnalysis.analysis.attendance_score)}`}>{employeeAnalysis.analysis.attendance_score}</p>
                <p className="text-sm text-slate-500 mt-1">Attendance Score</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                <p className={`text-4xl font-bold ${getScoreColor(employeeAnalysis.analysis.productivity_score)}`}>{employeeAnalysis.analysis.productivity_score}</p>
                <p className="text-sm text-slate-500 mt-1">Productivity Score</p>
                <Badge className={`mt-2 ${employeeAnalysis.analysis.promotion_readiness === 'Ready' ? 'bg-emerald-100 text-emerald-700' : employeeAnalysis.analysis.promotion_readiness === 'Almost Ready' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'}`}>
                  Promotion: {employeeAnalysis.analysis.promotion_readiness}
                </Badge>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-white rounded-lg">
                <h4 className="font-semibold text-emerald-700 mb-2 flex items-center gap-1"><Star className="h-4 w-4" /> Strengths</h4>
                <ul className="space-y-1">{employeeAnalysis.analysis.strengths?.map((s, i) => (<li key={i} className="text-sm text-slate-600 flex items-start gap-2"><ChevronRight className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" /> {s}</li>))}</ul>
              </div>
              <div className="p-4 bg-white rounded-lg">
                <h4 className="font-semibold text-amber-700 mb-2 flex items-center gap-1"><AlertCircle className="h-4 w-4" /> Areas of Improvement</h4>
                <ul className="space-y-1">{employeeAnalysis.analysis.areas_of_improvement?.map((a, i) => (<li key={i} className="text-sm text-slate-600 flex items-start gap-2"><ChevronRight className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" /> {a}</li>))}</ul>
              </div>
            </div>
            <div className="mt-4 p-4 bg-indigo-50 rounded-lg">
              <p className="text-sm text-indigo-800"><strong>Key Insights:</strong> {employeeAnalysis.analysis.key_insights}</p>
              <p className="text-sm text-indigo-700 mt-2"><strong>Recommendation:</strong> {employeeAnalysis.analysis.recommendation}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Review Draft Dialog */}
      <Dialog open={detailDialog === 'review-draft'} onOpenChange={() => setDetailDialog(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-blue-600" /> AI Review Draft: {reviewDraft?.employee_name}</DialogTitle></DialogHeader>
          {reviewDraft?.draft && (
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 rounded-lg"><h4 className="font-semibold mb-2">Summary</h4><p className="text-sm">{reviewDraft.draft.summary}</p></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-emerald-50 rounded-lg"><h4 className="font-semibold text-emerald-700 text-sm mb-2">Strengths</h4>{reviewDraft.draft.strengths?.map((s, i) => (<p key={i} className="text-xs text-slate-600 mb-1">- {s}</p>))}</div>
                <div className="p-3 bg-amber-50 rounded-lg"><h4 className="font-semibold text-amber-700 text-sm mb-2">Areas of Improvement</h4>{reviewDraft.draft.areas_of_improvement?.map((a, i) => (<p key={i} className="text-xs text-slate-600 mb-1">- {a}</p>))}</div>
              </div>
              <div className="p-3 bg-indigo-50 rounded-lg"><h4 className="font-semibold text-sm mb-2">Achievements</h4>{reviewDraft.draft.achievements?.map((a, i) => (<p key={i} className="text-xs mb-1">- {a}</p>))}</div>
              <div className="p-3 bg-purple-50 rounded-lg"><h4 className="font-semibold text-sm mb-2">Goals for Next Period</h4>{reviewDraft.draft.goals_for_next_period?.map((g, i) => (<p key={i} className="text-xs mb-1">- {g}</p>))}</div>
              <div className="p-3 bg-slate-50 rounded-lg"><h4 className="font-semibold text-sm mb-2">Training Recommendations</h4>{reviewDraft.draft.training_recommendations?.map((t, i) => (<p key={i} className="text-xs mb-1">- {t}</p>))}</div>
              <div className="flex items-center gap-4 p-3 bg-white border rounded-lg">
                <div><span className="text-sm text-slate-500">Suggested Rating:</span><span className="ml-2 font-bold text-lg">{reviewDraft.draft.suggested_rating}/10</span></div>
                <div><span className="text-sm text-slate-500">Promotion:</span><Badge className="ml-2">{reviewDraft.draft.promotion_recommendation}</Badge></div>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg"><h4 className="font-semibold text-sm mb-2">Overall Comments</h4><p className="text-sm text-slate-700">{reviewDraft.draft.overall_comments}</p></div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Sentiment Dialog */}
      <Dialog open={detailDialog === 'sentiment'} onOpenChange={() => setDetailDialog(null)}>
        <DialogContent className="max-w-2xl" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><MessageSquare className="h-5 w-5 text-amber-600" /> Sentiment Analysis</DialogTitle></DialogHeader>
          {sentimentResult?.sentiment && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                <div className="text-center">
                  <p className="text-3xl font-bold">{sentimentResult.sentiment.sentiment_score}/10</p>
                  <Badge className={sentimentResult.sentiment.overall_sentiment === 'positive' ? 'bg-emerald-100 text-emerald-700' : sentimentResult.sentiment.overall_sentiment === 'negative' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                    {sentimentResult.sentiment.overall_sentiment}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm">Morale: <strong>{sentimentResult.sentiment.employee_morale}</strong></p>
                  <p className="text-sm">Sources: {sentimentResult.feedback_sources}</p>
                  <p className="text-sm">Confidence: {Math.round((sentimentResult.sentiment.confidence || 0) * 100)}%</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-emerald-50 rounded-lg"><h4 className="text-sm font-semibold text-emerald-700 mb-2">Positive Signals</h4>{sentimentResult.sentiment.positive_signals?.map((s, i) => (<p key={i} className="text-xs mb-1">+ {s}</p>))}</div>
                <div className="p-3 bg-red-50 rounded-lg"><h4 className="text-sm font-semibold text-red-700 mb-2">Negative Signals</h4>{sentimentResult.sentiment.negative_signals?.map((s, i) => (<p key={i} className="text-xs mb-1">- {s}</p>))}</div>
              </div>
              {sentimentResult.sentiment.hr_action_needed && (
                <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg"><h4 className="text-sm font-semibold text-orange-700">HR Action Needed</h4><p className="text-xs text-orange-600 mt-1">{sentimentResult.sentiment.hr_action_reason}</p></div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* 360 Summary Dialog */}
      <Dialog open={detailDialog === '360-summary'} onOpenChange={() => setDetailDialog(null)}>
        <DialogContent className="max-w-2xl" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><GitBranch className="h-5 w-5 text-purple-600" /> 360 Summary: {summaryResult?.employee_name}</DialogTitle></DialogHeader>
          {summaryResult?.analysis && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                {['leadership_score', 'communication_score', 'technical_skills_score', 'teamwork_score', 'initiative_score', 'reliability_score'].map(key => (
                  <div key={key} className="text-center p-3 bg-slate-50 rounded-lg">
                    <p className={`text-2xl font-bold ${getScoreColor((summaryResult.analysis[key] || 0) * 10)}`}>{summaryResult.analysis[key]}</p>
                    <p className="text-xs text-slate-500 capitalize">{key.replace('_score', '').replace('_', ' ')}</p>
                  </div>
                ))}
              </div>
              <div className="p-4 bg-purple-50 rounded-lg text-center">
                <p className="text-4xl font-bold text-purple-700">{summaryResult.analysis.overall_360_score}/10</p>
                <p className="text-sm text-slate-500 mt-1">Overall 360 Score</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg"><p className="text-sm">{summaryResult.analysis.summary}</p></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-amber-50 rounded-lg"><h4 className="text-sm font-semibold text-amber-700 mb-2">Blind Spots</h4>{summaryResult.analysis.blind_spots?.map((b, i) => (<p key={i} className="text-xs mb-1">- {b}</p>))}</div>
                <div className="p-3 bg-emerald-50 rounded-lg"><h4 className="text-sm font-semibold text-emerald-700 mb-2">Hidden Strengths</h4>{summaryResult.analysis.hidden_strengths?.map((s, i) => (<p key={i} className="text-xs mb-1">- {s}</p>))}</div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Growth Prediction Dialog */}
      <Dialog open={detailDialog === 'growth'} onOpenChange={() => setDetailDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><TrendingUp className="h-5 w-5 text-emerald-600" /> Growth Prediction: {growthResult?.employee_name}</DialogTitle></DialogHeader>
          {growthResult?.analysis && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-emerald-50 rounded-lg text-center">
                  <p className="text-sm text-slate-500">Promotion Readiness</p>
                  <p className="text-xl font-bold text-emerald-700">{growthResult.analysis.promotion_readiness}</p>
                  <p className="text-sm text-slate-500 mt-1">Probability: {growthResult.analysis.promotion_probability}%</p>
                </div>
                <div className="p-4 bg-indigo-50 rounded-lg text-center">
                  <p className="text-sm text-slate-500">Leadership Potential</p>
                  <p className="text-xl font-bold text-indigo-700">{growthResult.analysis.leadership_potential}</p>
                  <p className="text-sm text-slate-500 mt-1">Trajectory: {growthResult.analysis.growth_trajectory}</p>
                </div>
              </div>
              <div className="p-3 bg-amber-50 rounded-lg">
                <p className="text-sm"><strong>Retention Risk:</strong> {growthResult.analysis.retention_risk}</p>
                <p className="text-sm mt-1"><strong>Career Path:</strong> {growthResult.analysis.career_path_suggestion}</p>
              </div>
              {growthResult.analysis.skill_gaps?.length > 0 && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <h4 className="text-sm font-semibold mb-2">Skill Gaps</h4>
                  {growthResult.analysis.skill_gaps.map((gap, i) => (
                    <div key={i} className="flex items-center justify-between text-xs p-2 bg-white rounded mb-1">
                      <span className="font-medium">{gap.skill}</span>
                      <span>{gap.current_level} to {gap.required_level}</span>
                      <Badge className={gap.priority === 'High' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>{gap.priority}</Badge>
                    </div>
                  ))}
                </div>
              )}
              {growthResult.analysis.recommended_trainings?.length > 0 && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <h4 className="text-sm font-semibold mb-2">Recommended Trainings</h4>
                  {growthResult.analysis.recommended_trainings.map((t, i) => (
                    <div key={i} className="flex items-center justify-between text-xs p-2 bg-white rounded mb-1">
                      <span>{t.title}</span>
                      <div className="flex gap-1"><Badge variant="outline">{t.type}</Badge><Badge variant="outline">{t.urgency}</Badge></div>
                    </div>
                  ))}
                </div>
              )}
              <div className="p-3 bg-indigo-50 rounded-lg"><p className="text-sm"><strong>Prediction:</strong> {growthResult.analysis.key_prediction}</p></div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* External Metrics Dialog */}
      <Dialog open={externalMetricsDialog} onOpenChange={setExternalMetricsDialog}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader><DialogTitle>Add External Metrics: {selectedEmployee?.name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div><Label>Sales Numbers</Label><Input type="number" value={extMetrics.sales_numbers} onChange={(e) => setExtMetrics(p => ({ ...p, sales_numbers: e.target.value }))} placeholder="e.g., 50000" autoComplete="off" /></div>
            <div><Label>Project Delivery Rate (%)</Label><Input type="number" value={extMetrics.project_delivery_rate} onChange={(e) => setExtMetrics(p => ({ ...p, project_delivery_rate: e.target.value }))} placeholder="e.g., 95" autoComplete="off" /></div>
            <div><Label>Client Feedback (1-10)</Label><Input type="number" value={extMetrics.client_feedback_score} onChange={(e) => setExtMetrics(p => ({ ...p, client_feedback_score: e.target.value }))} placeholder="e.g., 8.5" autoComplete="off" /></div>
            <div><Label>Task Completion (%)</Label><Input type="number" value={extMetrics.task_completion_rate} onChange={(e) => setExtMetrics(p => ({ ...p, task_completion_rate: e.target.value }))} placeholder="e.g., 92" autoComplete="off" /></div>
            <div className="col-span-2"><Label>Notes</Label><Textarea value={extMetrics.notes} onChange={(e) => setExtMetrics(p => ({ ...p, notes: e.target.value }))} placeholder="Additional context..." autoComplete="off" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setExternalMetricsDialog(false)}>Cancel</Button><Button onClick={saveExternalMetrics} className="bg-indigo-600 hover:bg-indigo-700">Save Metrics</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Loading Overlay */}
      {analyzing && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50" data-testid="ai-loading">
          <div className="bg-white rounded-xl p-6 shadow-2xl flex items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
            <div><p className="font-semibold">AI is analyzing...</p><p className="text-sm text-slate-500">This may take a few seconds</p></div>
          </div>
        </div>
      )}
    </div>
  );
};
